import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  analyzeBusinessOpportunity,
  analyzeBusinessOpportunityErrorMessage,
} from '../lib/analyzeBusinessOpportunity';
import {
  analyzeFounderUpdate,
  analyzeFounderUpdateErrorMessage,
} from '../lib/analyzeFounderUpdate';
import {
  analyzeInvestmentCase,
  analyzeInvestmentCaseErrorMessage,
} from '../lib/analyzeInvestmentCase';
import { saveBusinessOpportunity } from '../lib/saveBusinessOpportunity';
import type {
  BusinessOpportunity,
  FounderUpdateIntelligence,
  InvestmentCase,
  MonthlyUpdate,
} from '../types';
import { formatMonthYear } from '../utils/formatters';
import { newId } from '../utils/id';
import { buildOpportunityIteration } from '../utils/opportunityIterations';
import { useAppData } from './AppDataContext';
import { useUser } from './UserContext';

export type AnalysisJobKind = 'founderUpdate' | 'deck' | 'idea';

export type AnalysisJob = {
  id: string;
  kind: AnalysisJobKind;
  /** Report / case / opportunity id the analysis runs against. */
  targetId: string;
  /** Human label shown in the toast, e.g. "Acme · March 2026". */
  targetLabel: string;
  /** In-app path the toast links to while the job runs. */
  link: string;
  status: 'loading' | 'error';
  error: string | null;
};

type AnalysisJobsContextValue = {
  jobs: AnalysisJob[];
  isAnalyzing: (kind: AnalysisJobKind, targetId: string) => boolean;
  getJobError: (kind: AnalysisJobKind, targetId: string) => string | null;
  startFounderUpdateAnalysis: (reportId: string) => Promise<void>;
  startDeckAnalysis: (caseId: string) => Promise<void>;
  startIdeaAnalysis: (opportunityId: string) => Promise<void>;
  dismissError: (jobId: string) => void;
};

const AnalysisJobsContext = createContext<AnalysisJobsContextValue | undefined>(undefined);

function runningKey(kind: AnalysisJobKind, targetId: string): string {
  return `${kind}:${targetId}`;
}

function mergeAnalysisOntoReport(
  report: MonthlyUpdate,
  updateIntelligence: FounderUpdateIntelligence
): MonthlyUpdate {
  const metrics = updateIntelligence.extractedMetrics;
  return {
    ...report,
    updateIntelligence,
    updatedAt: new Date().toISOString(),
    revenue: report.revenue ?? metrics.revenue,
    burn: report.burn ?? metrics.burn,
    remainingCash: report.remainingCash ?? metrics.remainingCash,
  };
}

export function AnalysisJobsProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const {
    companies,
    monthlyUpdates,
    setMonthlyUpdates,
    investmentCases,
    setInvestmentCases,
    businessOpportunities,
  } = useAppData();

  const companiesRef = useRef(companies);
  companiesRef.current = companies;
  const monthlyUpdatesRef = useRef(monthlyUpdates);
  monthlyUpdatesRef.current = monthlyUpdates;
  const investmentCasesRef = useRef(investmentCases);
  investmentCasesRef.current = investmentCases;
  const opportunitiesRef = useRef(businessOpportunities);
  opportunitiesRef.current = businessOpportunities;

  const [jobs, setJobs] = useState<AnalysisJob[]>([]);
  const runningKeysRef = useRef(new Set<string>());

  const isAnalyzing = useCallback(
    (kind: AnalysisJobKind, targetId: string) =>
      jobs.some(
        (job) => job.kind === kind && job.targetId === targetId && job.status === 'loading'
      ),
    [jobs]
  );

  const getJobError = useCallback(
    (kind: AnalysisJobKind, targetId: string) => {
      const job = jobs.find(
        (entry) => entry.kind === kind && entry.targetId === targetId && entry.status === 'error'
      );
      return job?.error ?? null;
    },
    [jobs]
  );

  const dismissError = useCallback((jobId: string) => {
    setJobs((current) => current.filter((job) => job.id !== jobId));
  }, []);

  const runJob = useCallback(
    async (
      kind: AnalysisJobKind,
      targetId: string,
      targetLabel: string,
      link: string,
      run: () => Promise<void>,
      errorMessage: (err: unknown) => string
    ) => {
      const key = runningKey(kind, targetId);
      if (runningKeysRef.current.has(key)) return;

      runningKeysRef.current.add(key);
      const jobId = newId();
      setJobs((current) => [
        ...current.filter(
          (job) => !(job.kind === kind && job.targetId === targetId && job.status === 'error')
        ),
        { id: jobId, kind, targetId, targetLabel, link, status: 'loading', error: null },
      ]);

      try {
        await run();
        setJobs((currentJobs) => currentJobs.filter((job) => job.id !== jobId));
      } catch (err) {
        const message = errorMessage(err);
        setJobs((currentJobs) =>
          currentJobs.map((job) =>
            job.id === jobId ? { ...job, status: 'error', error: message } : job
          )
        );
      } finally {
        runningKeysRef.current.delete(key);
      }
    },
    []
  );

  const startFounderUpdateAnalysis = useCallback(
    async (reportId: string) => {
      const trimmedId = reportId.trim();
      if (!trimmedId) return;

      const report = monthlyUpdatesRef.current.find((entry) => entry.id === trimmedId);
      if (!report) return;

      const company = companiesRef.current.find((entry) => entry.id === report.companyId);
      const label = [company?.name?.trim(), formatMonthYear(report.periodMonth)]
        .filter(Boolean)
        .join(' · ');

      await runJob(
        'founderUpdate',
        trimmedId,
        label || 'Monthly report',
        `/investments/${report.companyId}?tab=reports`,
        async () => {
          const updateIntelligence = await analyzeFounderUpdate(trimmedId);
          setMonthlyUpdates((prev) =>
            prev.map((p) => (p.id === trimmedId ? mergeAnalysisOntoReport(p, updateIntelligence) : p))
          );
        },
        analyzeFounderUpdateErrorMessage
      );
    },
    [runJob, setMonthlyUpdates]
  );

  const startDeckAnalysis = useCallback(
    async (caseId: string) => {
      const trimmedId = caseId.trim();
      if (!trimmedId) return;

      const investmentCase = investmentCasesRef.current.find((entry) => entry.id === trimmedId);
      if (!investmentCase) return;

      await runJob(
        'deck',
        trimmedId,
        investmentCase.name?.trim() || 'Investment case',
        `/investment-cases/${trimmedId}?tab=intelligence`,
        async () => {
          const deckIntelligence = await analyzeInvestmentCase(trimmedId);
          setInvestmentCases((prev) =>
            prev.map((p): InvestmentCase =>
              p.id === trimmedId
                ? { ...p, deckIntelligence, updatedAt: new Date().toISOString() }
                : p
            )
          );
        },
        analyzeInvestmentCaseErrorMessage
      );
    },
    [runJob, setInvestmentCases]
  );

  const startIdeaAnalysis = useCallback(
    async (opportunityId: string) => {
      const trimmedId = opportunityId.trim();
      if (!trimmedId || !userId) return;

      const opportunity = opportunitiesRef.current?.find((entry) => entry.id === trimmedId);
      if (!opportunity) return;

      await runJob(
        'idea',
        trimmedId,
        opportunity.name?.trim() || 'Opportunity',
        `/opportunities/${trimmedId}?tab=analysis`,
        async () => {
          const hadIntelligence = Boolean(opportunity.ideaIntelligence);
          const ideaIntelligence = await analyzeBusinessOpportunity(trimmedId);
          const current = opportunitiesRef.current?.find((entry) => entry.id === trimmedId);
          if (!current) return;

          const iteration = buildOpportunityIteration(
            'analyze',
            hadIntelligence ? 'Re-analyzed the idea.' : 'Initial analysis of the idea.',
            ideaIntelligence
          );
          const next: BusinessOpportunity = {
            ...current,
            ideaIntelligence,
            iterations: [...(current.iterations ?? []), iteration],
            status: current.status === 'draft' ? 'refining' : current.status,
            updatedAt: new Date().toISOString(),
          };
          await saveBusinessOpportunity(userId, {
            id: next.id,
            name: next.name,
            industry: next.industry,
            ideaSummary: next.ideaSummary,
            status: next.status,
            ideaIntelligence: next.ideaIntelligence,
            refinements: next.refinements,
            iterations: next.iterations,
            feedback: next.feedback,
            pnl: next.pnl,
            roadmap: next.roadmap,
            promotedCaseId: next.promotedCaseId,
            existing: current,
          });
        },
        analyzeBusinessOpportunityErrorMessage
      );
    },
    [runJob, userId]
  );

  const value = useMemo(
    () => ({
      jobs,
      isAnalyzing,
      getJobError,
      startFounderUpdateAnalysis,
      startDeckAnalysis,
      startIdeaAnalysis,
      dismissError,
    }),
    [
      jobs,
      isAnalyzing,
      getJobError,
      startFounderUpdateAnalysis,
      startDeckAnalysis,
      startIdeaAnalysis,
      dismissError,
    ]
  );

  return <AnalysisJobsContext.Provider value={value}>{children}</AnalysisJobsContext.Provider>;
}

export function useAnalysisJobs(): AnalysisJobsContextValue {
  const ctx = useContext(AnalysisJobsContext);
  if (!ctx) {
    throw new Error('useAnalysisJobs must be used within AnalysisJobsProvider');
  }
  return ctx;
}
